import { useState, useMemo } from "react";
import TagFilterBar from "../Components/TagFilterBar";
import { questionMatchesSteps } from "../Components/TagFilterUtils";

/**
 * Searchable KPI checkbox list (asset-view add panels).
 *
 * `candidates`: [{ kpiId, kpiTitle, tags, locked, lockedLabel }] — locked rows
 * show a lock tag and can't be ticked (their existing assets use the other
 * scope). Filtering mirrors the page-level bar: the same TagFilterBar on the
 * candidates' tags, plus a title search.
 */
export default function KpiPickerList({
  candidates,
  selectedIds,
  onToggle,
  disabled,
  idBase,
  emptyText = "No KPIs available.",
}) {
  const [search, setSearch] = useState("");
  const [filterSteps, setFilterSteps] = useState([]);

  const visible = useMemo(() => {
    let list = candidates.filter(c => questionMatchesSteps(c, filterSteps));
    const s = search.trim().toLowerCase();
    if (s) list = list.filter(c => (c.kpiTitle || "").toLowerCase().includes(s));
    return list;
  }, [candidates, filterSteps, search]);

  if (candidates.length === 0) {
    return (
      <div style={{ fontSize: 11.5, color: "var(--kpa-slate-400)", padding: "8px 2px" }}>
        {emptyText}
      </div>
    );
  }

  return (
    <>
      <div style={{ position: "relative", marginBottom: 8 }}>
        <i className="fas fa-search" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: "var(--kpa-slate-400)", pointerEvents: "none" }} aria-hidden="true" />
        <input
          className="kpa-input"
          style={{ paddingLeft: 28 }}
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search KPIs…"
          aria-label="Search KPIs"
        />
      </div>

      <div style={{ marginBottom: 8 }}>
        <TagFilterBar
          allQuestions={candidates}
          filterSteps={filterSteps}
          onFilterStepsChange={setFilterSteps}
          pendingIds={selectedIds}
          disabled={disabled}
        />
      </div>

      <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid var(--kpa-slate-200)", borderRadius: 8 }}>
        {visible.length === 0 ? (
          <div style={{ fontSize: 11.5, color: "var(--kpa-slate-400)", padding: 10 }}>
            No KPIs match this search or filter.
          </div>
        ) : visible.map((c, idx) => (
          <label
            key={c.kpiId}
            htmlFor={`${idBase}-${c.kpiId}`}
            className="kpa-kpi-row-header"
            style={{
              padding: "8px 12px",
              borderBottom: idx === visible.length - 1 ? "none" : "1px solid var(--kpa-slate-100)",
              cursor: c.locked ? "not-allowed" : "pointer",
              margin: 0,
            }}
          >
            <input
              id={`${idBase}-${c.kpiId}`}
              type="checkbox"
              className="kpa-checkbox"
              style={{ width: 14, height: 14, flexShrink: 0 }}
              checked={selectedIds.has(c.kpiId)}
              disabled={c.locked || disabled}
              onChange={() => onToggle(c.kpiId)}
            />
            <span className="kpa-kpi-title" style={{ flex: 1, minWidth: 0 }} title={c.kpiTitle}>
              {c.kpiTitle || "—"}
            </span>
            {c.locked && (
              <span className="kpa-tag kpa-tag--muted" title="Locked: this KPI's existing assets use the other scope — scopes can't be mixed.">
                <i className="fas fa-lock" style={{ marginRight: 3, fontSize: 8 }} aria-hidden="true" />
                {c.lockedLabel}
              </span>
            )}
          </label>
        ))}
      </div>
    </>
  );
}
